import * as React from 'react';
import { Search, X } from 'lucide-react';
import { useDepartmentLocale } from '../../ui/locale/departmentLocale';
import { useOptionalLanguage } from '../../context/LanguageContext';
import { translateKey } from '../../ui/locale/centralLocalization';
import './GlobalSearchInput.css';

export interface GlobalSearchInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  size?: 'small' | 'middle' | 'large' | number;
  inputClassName?: string;
  inputStyle?: React.CSSProperties;
  /** Submit searches keep their existing commit callback; clearing commits an empty search. */
  onSearch?: (value: string) => void;
  onClear?: () => void;
  clearLabel?: string;
  enterButton?: React.ReactNode;
  suffix?: React.ReactNode;
}

/** Presentation and text clearing only. Callers own filtering, requests and debounce. */
export const GlobalSearchInput = React.forwardRef<HTMLInputElement, GlobalSearchInputProps>(function GlobalSearchInput({
  value, defaultValue, onChange, onInput, onSearch, onClear, clearLabel, enterButton, suffix,
  className = '', style, inputClassName, inputStyle, size = 'middle', disabled, readOnly,
  onKeyDown, onKeyDownCapture, onCompositionStart, onCompositionEnd, ...props
}, forwardedRef) {
  const locale = useDepartmentLocale();
  const appLocale = useOptionalLanguage();
  const language = locale?.language ?? appLocale?.language ?? 'en';
  const searchLabel = translateKey('common.search', language);
  const [localValue, setLocalValue] = React.useState(defaultValue ?? '');
  const currentValue = value === undefined ? localValue : value ?? '';
  const hasText = String(currentValue).length > 0;
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const composing = React.useRef(false);
  const setInputRef = React.useCallback((input: HTMLInputElement | null) => {
    inputRef.current = input;
    if (typeof forwardedRef === 'function') forwardedRef(input);
    else if (forwardedRef) forwardedRef.current = input;
  }, [forwardedRef]);

  React.useEffect(() => {
    const input = inputRef.current;
    if (!input || props['aria-label'] || props['aria-labelledby']) return;
    const label = Array.from(input.labels ?? []).map(element => {
      const copy = element.cloneNode(true) as HTMLLabelElement;
      copy.querySelectorAll('.crm-global-search,button,input,select,textarea,svg,kbd').forEach(node => node.remove());
      return copy.getAttribute('aria-label') ?? copy.textContent?.replace(/\s+/g, ' ').trim() ?? '';
    }).filter(Boolean).join(' ');
    input.setAttribute('aria-label', label || props.placeholder || searchLabel);
  });

  React.useEffect(() => {
    const form = inputRef.current?.form;
    if (!form || value !== undefined) return;
    const reset = () => setLocalValue(defaultValue ?? '');
    form.addEventListener('reset', reset);
    return () => form.removeEventListener('reset', reset);
  }, [value, defaultValue]);

  const clear = (event: React.SyntheticEvent) => {
    const input = inputRef.current;
    if (!input || !input.value || disabled || readOnly || input.matches(':disabled')) return;
    // Keep the native target (name, form, validity and focus) for existing callbacks.
    input.value = '';
    if (value === undefined) setLocalValue('');
    const changeEvent = Object.assign(Object.create(event), { type: 'change', target: input, currentTarget: input }) as React.ChangeEvent<HTMLInputElement>;
    onInput?.(Object.assign(Object.create(changeEvent), { data: '', nativeEvent: new InputEvent('input', { inputType: 'deleteContentBackward' }) }));
    onChange?.(changeEvent);
    onSearch?.('');
    onClear?.();
    input.focus();
  };

  return <span dir={props.dir} className={`crm-global-search crm-global-search--${typeof size === 'number' ? 'middle' : size} ${className}`} style={style} data-disabled={disabled || undefined}>
    {(!onSearch || enterButton) && <Search className="crm-global-search__icon" size={17} aria-hidden="true" />}
    <input {...props} ref={setInputRef} className={inputClassName} style={inputStyle}
      type={props.type ?? 'text'} size={typeof size === 'number' ? size : undefined}
      aria-label={props['aria-label']}
      data-aa-search-input="true" value={currentValue} disabled={disabled} readOnly={readOnly}
      onChange={event => { if (value === undefined) setLocalValue(event.target.value); onChange?.(event); }}
      onInput={onInput}
      onCompositionStart={event => { composing.current = true; onCompositionStart?.(event); }}
      onCompositionEnd={event => { composing.current = false; onCompositionEnd?.(event); }}
      onKeyDownCapture={event => {
        const ime = composing.current || event.nativeEvent.isComposing || event.keyCode === 229;
        if (ime && event.key === 'Escape') { event.stopPropagation(); return; }
        if (!ime && event.key === 'Escape' && event.currentTarget.value && !disabled && !readOnly && !event.currentTarget.matches(':disabled')) {
          event.preventDefault(); event.stopPropagation(); clear(event); return;
        }
        onKeyDownCapture?.(event);
      }}
      onKeyDown={event => {
        const ime = composing.current || event.nativeEvent.isComposing || event.keyCode === 229;
        onKeyDown?.(event);
        if (!ime && event.key === 'Enter' && onSearch && !event.defaultPrevented) {
          event.preventDefault(); onSearch(event.currentTarget.value);
        }
      }} />
    {hasText && !disabled && !readOnly && <button className="crm-global-search__clear" type="button" aria-label={clearLabel ?? translateKey('common.clearSearch',language)}
      onMouseDown={event => event.preventDefault()} onClick={clear}><X size={17} aria-hidden="true" /></button>}
    {onSearch && !enterButton && <button className="crm-global-search__submit crm-global-search__submit--icon" type="button" aria-label={searchLabel} disabled={disabled}
      onClick={() => onSearch(String(inputRef.current?.value ?? currentValue))}><Search size={17} aria-hidden="true" /></button>}
    {onSearch && enterButton && <button className="crm-global-search__submit" type="button" disabled={disabled} aria-label={typeof enterButton === 'string' ? enterButton : searchLabel}
      onClick={() => onSearch(String(inputRef.current?.value ?? currentValue))}>{enterButton === true ? <Search size={17} aria-hidden="true" /> : enterButton}</button>}
    {suffix}
  </span>;
});
